/**
 * TanStack Query hooks for the access-request flow (ADR-0015).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccessRequestCreateInput,
  AccessRequestListResponse,
  AdminUser,
  AdminUserUpdateInput,
  DirectorySearchResponse,
  DirectoryUserCreateInput,
  MyAccess,
  Role,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';

const KEY = ['access'] as const;

/** The caller's own access state — works for pending/denied/unregistered. */
export function useMyAccess() {
  return useQuery<MyAccess>({
    queryKey: [...KEY, 'me'],
    queryFn: () => apiCall<MyAccess>('/access-requests/me'),
  });
}

export function useSubmitAccessRequest() {
  const qc = useQueryClient();
  return useMutation<MyAccess, Error, AccessRequestCreateInput>({
    mutationFn: (body) => apiCall<MyAccess>('/access-requests', { method: 'POST', body }),
    onSuccess: (res) => {
      qc.setQueryData([...KEY, 'me'], res);
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useAccessRequests(status: 'pending' | 'active' | 'denied') {
  return useQuery<AccessRequestListResponse>({
    queryKey: [...KEY, 'admin', status],
    queryFn: () =>
      apiCall<AccessRequestListResponse>(`/admin/access-requests?status=${status}`),
  });
}

export function useApproveAccessRequest() {
  const qc = useQueryClient();
  return useMutation<MyAccess, Error, { userId: string; role?: Role }>({
    mutationFn: ({ userId, role }) =>
      apiCall<MyAccess>(`/admin/access-requests/${userId}/approve`, {
        method: 'POST',
        body: role ? { role } : {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'admin'] }),
  });
}

export function useDenyAccessRequest() {
  const qc = useQueryClient();
  return useMutation<MyAccess, Error, string>({
    mutationFn: (userId) =>
      apiCall<MyAccess>(`/admin/access-requests/${userId}/deny`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'admin'] }),
  });
}

// ---- Admin: all-user role + lifecycle management (RoleMgmt.2) ----
export function useAdminUsers() {
  return useQuery<{ data: AdminUser[] }>({
    queryKey: [...KEY, 'admin', 'users'],
    queryFn: () => apiCall<{ data: AdminUser[] }>('/admin/users'),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<
    AdminUser,
    Error,
    { userId: string; body: AdminUserUpdateInput }
  >({
    mutationFn: ({ userId, body }) =>
      apiCall<AdminUser>(`/admin/users/${userId}`, { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY, 'admin'] });
      qc.invalidateQueries({ queryKey: ['me'] }); // a self role-change updates the shell
    },
  });
}

// ---- Admin: add a user straight from the Entra directory ----

/** Live Entra-directory search (debounce `q` at the call site). Only runs when
 *  `enabled` (the dialog is open) so we don't hit Graph in the background. */
export function useDirectorySearch(q: string, enabled: boolean) {
  return useQuery<DirectorySearchResponse>({
    queryKey: [...KEY, 'admin', 'directory', q],
    queryFn: () =>
      apiCall<DirectorySearchResponse>(`/admin/directory/search?q=${encodeURIComponent(q)}`),
    enabled,
    staleTime: 60_000,
  });
}

export function useCreateUserFromDirectory() {
  const qc = useQueryClient();
  return useMutation<AdminUser, Error, DirectoryUserCreateInput>({
    mutationFn: (body) =>
      apiCall<AdminUser>('/admin/users/from-directory', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'admin'] }),
  });
}
